import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {fundraisersApi} from '../api';
import {CommunicationMessagesFilter, CommunicationMessagesWithMetaModel} from '../../communication';
import {useCallback, useMemo, useState} from 'react';

type FundraiserCommunicationState = {
  getInfiniteMessages: UseInfiniteQueryResult<InfiniteData<CommunicationMessagesWithMetaModel>, Error>;
  filters: CommunicationMessagesFilter;
  updateFilter: (newFilter: Partial<CommunicationMessagesFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useFundraiserCommunication = (
  appealId: string,
  initialFilter: CommunicationMessagesFilter = {},
): FundraiserCommunicationState => {
  const [filters, setFilters] = useState<CommunicationMessagesFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteMessages = useInfiniteQuery<
    CommunicationMessagesWithMetaModel,
    Error,
    InfiniteData<CommunicationMessagesWithMetaModel>,
    ['fundraiser-communication', string, Omit<CommunicationMessagesFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['fundraiser-communication', appealId, infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return fundraisersApi.getCommunicationMessages(appealId, {
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
    enabled: !!appealId,
  });

  const updateFilter = useCallback((newFilter: Partial<CommunicationMessagesFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfiniteMessages,
    filters,
    updateFilter,
  };
};
